/**
 * The four library views: Labs, Scenarios, Configuration Items, Devices.
 *
 * Each is `LabLibraryList` plus the answers to three questions only this tier can answer: what
 * usefully narrows these rows, what a row *is* in two or three columns, and how far editing one
 * reaches. Nothing about the table itself lives here - that is the point of having one table.
 *
 * The usage lists are the reason these views join more than they list. Every one of these tables is
 * shared by design, and the row itself only carries a count: `attachment_count` on an item,
 * `device_count` on a type. A count says an edit is risky without saying what it would reach, so
 * each view loads the link table alongside its rows and turns the count into a list of names. The
 * link tables are small - one row per attachment, one per section - and they are what the builder
 * loads anyway.
 *
 * Every view is read-and-lifecycle only: it opens the editor the shell owns, and it can disable,
 * duplicate or delete a row. What a row *contains* is edited in the builder, because the tree is
 * where the containment is visible.
 */

import React, { useMemo } from 'react';
import {
  arrowSquareOut,
  copy,
  eye,
  eyeSlash,
  pencilSimple,
  trash,
} from '@harbor/elements/icons';
import {
  useDeleteLabRow,
  useLabAttachments,
  useLabConfigurationItems,
  useLabConfigurations,
  useLabDataCenters,
  useLabDeviceTypes,
  useLabDevices,
  useLabScenarioLinks,
  useLabScenarios,
  useLabs,
  useSaveLabRow,
} from '../../hooks/lab-hooks';
import {
  Lab,
  LabConfiguration,
  LabConfigurationItem,
  LabOption,
  LabScenario,
  LabDevice,
  LabScenarioItem,
  LabScenarioLink,
  LAB_ITEM_OPERATIONS,
  LAB_SIZES,
  LAB_TABLES,
  LAB_TRANSPORTS,
  labDemoSummary,
  labDeviceBlockers,
  labItemBlockers,
  labItemOperationLabel,
  labItemSummary,
  labSizeLabel,
  labTransportLabel,
} from '../../support/labService';
import { LabStatusTag } from './LabShared';
import {
  LabAuthoringStatus,
  LabRowAction,
  LabUsage,
  useLabToasts,
} from './LabAdminShared';
import LabLibraryList, { LabLibraryFacet } from './LabLibraryList';

/* ------------------------------------------------------------------------- *
 * Shared plumbing
 * ------------------------------------------------------------------------- */

/**
 * Where a row goes when it is opened.
 *
 * Supplied by the shell rather than routed from here: the four views are also rendered inside the
 * builder's pickers, where "open" means select rather than navigate.
 */
export type LabLibraryNav = {
  openLab?: (labId: number) => void;
  openScenario?: (scenarioId: number) => void;
  openItem?: (itemId: number) => void;
  openDevice?: (deviceId: number) => void;
};

type LibraryViewProps<T> = LabLibraryNav & {
  /** Authoring. The API refuses either way; this decides whether the actions are offered. */
  canEdit?: boolean;
  /** Opens the create form for this tier. Omit and the New button is not shown. */
  onNew?: () => void;
  /** Opens the edit form for one row. Omit and Edit is not offered. */
  onEdit?: (row: T) => void;
  selectedId?: number | null;
  /**
   * The shell's search term, when it owns one.
   *
   * Lifted so the single box in the top bar narrows whichever of the four lists is showing, and so
   * the shell can count what the same term matches in the three that are not.
   */
  searchTerm?: string;
  onSearchTerm?: (term: string) => void;
};

/**
 * The dot on a library row.
 *
 * `fatal` is passed in rather than derived from the number of blockers, because severity here is
 * about kind, not count: "no dCloud demo recorded" and "will never be matched to a session" are
 * both one blocker, and only one of them means the row cannot work at all.
 */
const authoringStatus = (
  enabled: boolean,
  blockers: string[],
  fatal: boolean,
  retiredTitle: string,
): { status: LabAuthoringStatus; title?: string } => {
  if (!enabled) return { status: 'retired', title: retiredTitle };
  if (fatal) return { status: 'failing', title: blockers.join(' ') };
  if (blockers.length) return { status: 'running', title: blockers.join(' ') };

  return { status: 'healthy' };
};

/** Free-text match over whichever fields a view says are worth searching. */
const matchesAny = (needle: string, fields: (string | number | null | undefined)[]) =>
  fields.some((field) => String(field ?? '').toLowerCase().includes(needle));

/**
 * The four free-text predicates, exported because the shell counts with them too.
 *
 * One box in the top bar narrows the list that is showing, and it has to be able to say "four
 * matches in Devices" about the three that are not. Sharing the predicate is the only way that
 * count and the list it leads to can agree: a nav badge reading none, over a list that then finds
 * two rows, is worse than no badge at all.
 */
export const labMatchesLab = (lab: Lab, needle: string) =>
  matchesAny(needle, [
    lab.name,
    lab.slug,
    lab.description,
    lab.demo_name,
    lab.demo_uid,
    lab.owner_email,
  ]);

export const labMatchesScenario = (scenario: LabScenario, needle: string) =>
  matchesAny(needle, [
    scenario.name,
    scenario.slug,
    scenario.description,
    scenario.course_name,
    scenario.demo_name,
  ]);

export const labMatchesItem = (item: LabConfigurationItem, needle: string) =>
  matchesAny(needle, [
    item.name,
    item.key,
    item.description,
    item.device_type_name,
    item.device_type_key,
    labItemOperationLabel(item.operation),
  ]);

export const labMatchesDevice = (device: LabDevice, needle: string) =>
  matchesAny(needle, [
    device.display_name,
    device.device_key,
    device.platform,
    device.role,
    device.address_template,
    device.device_type_name,
    // Which product, for a family type. Somebody looking for "the ISE box" is searching the answer
    // to that question, not the type name every appliance shares.
    device.product_name,
    device.credential_ref,
  ]);

const updatedOf = (row: { last_updated?: string | null; [key: string]: any }) => row.last_updated;

/**
 * Disable, duplicate and delete, for one table.
 *
 * The three lifecycle actions are identical across the four tiers apart from what they take with
 * them, so the differences are parameters. Two deliberate choices:
 *
 * - "Archive" in the layout spec is `enabled = false` here. There is no archived flag in this
 *   schema, and inventing one in the UI would mean a row that reads as filed away and is in fact
 *   still attached to everything - so the label says archive, and the confirm says exactly what
 *   happens.
 * - `copyOf` names the writable fields explicitly rather than spreading the row. These rows arrive
 *   with flattened read-only fields on them (`device_type_name`, `attachment_count`), and posting
 *   those back is how a duplicate ends up carrying a count that belongs to its original.
 */
const useLifecycle = <T extends { id: number; enabled?: boolean }>(
  table: string,
  config: {
    nameOf: (row: T) => string;
    /** Said in the confirm. Everything here is gone the moment the row is. */
    deleteConsequence: string;
    /** A new row from an old one. Omit and Duplicate is not offered for this tier. */
    copyOf?: (row: T) => Record<string, any>;
    /** Why this row cannot be deleted right now. Keeps the entry visible with its reason. */
    deleteBlockedBy?: (row: T) => string | null;
  },
) => {
  const save = useSaveLabRow<Record<string, any> & { id?: number }>(table);
  const destroy = useDeleteLabRow(table);
  const toasts = useLabToasts();

  const setEnabled = async (rows: T[], enabled: boolean) => {
    try {
      await Promise.all(rows.map((row) => save.mutateAsync({ id: row.id, enabled })));
      toasts.saved(
        rows.length === 1
          ? `${config.nameOf(rows[0])} is ${enabled ? 'live again' : 'archived - still attached, delivers nothing'}.`
          : `${rows.length} rows ${enabled ? 'enabled' : 'archived'}.`,
      );
    } catch (exc) {
      toasts.failed('save that', exc);
    }
  };

  const remove = async (rows: T[]) => {
    const blocked = config.deleteBlockedBy
      ? rows.map((row) => [row, config.deleteBlockedBy?.(row)] as const).filter(([, why]) => !!why)
      : [];

    if (blocked.length) {
      // Refused here rather than sent and rejected: the API would say the same thing in less
      // useful words, and nothing should be half-deleted while the rest of a selection succeeds.
      toasts.failed(
        `delete ${config.nameOf(blocked[0][0])}${
          blocked.length > 1 ? ` (and ${blocked.length - 1} more of the selection)` : ''
        }`,
        new Error(blocked[0][1] as string),
      );

      return;
    }

    const subject =
      rows.length === 1 ? config.nameOf(rows[0]) : `${rows.length} rows`;

    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete ${subject}? ${config.deleteConsequence}`)) return;

    try {
      await Promise.all(rows.map((row) => destroy.mutateAsync(row.id)));
      toasts.removed(subject);
    } catch (exc) {
      toasts.failed('delete that', exc);
    }
  };

  const duplicate = async (rows: T[]) => {
    if (!config.copyOf) return;

    try {
      await Promise.all(rows.map((row) => save.mutateAsync(config.copyOf?.(row) as any)));
      toasts.saved(
        rows.length === 1
          ? `Copied ${config.nameOf(rows[0])}. The copy is disabled until you have edited it.`
          : `${rows.length} copies created, all disabled.`,
      );
    } catch (exc) {
      toasts.failed('copy that', exc);
    }
  };

  /** The tail of every row menu, after whatever the view puts at the top. */
  const rowActions = (row: T): LabRowAction[] => {
    const blockedWhy = config.deleteBlockedBy?.(row) || null;

    return [
      ...(config.copyOf
        ? [
            {
              key: 'duplicate',
              label: 'Duplicate',
              asset: copy,
              onSelect: () => void duplicate([row]),
            },
          ]
        : []),
      {
        key: 'enabled',
        label: row.enabled === false ? 'Restore' : 'Archive (disable)',
        asset: row.enabled === false ? eye : eyeSlash,
        onSelect: () => void setEnabled([row], row.enabled === false),
      },
      {
        key: 'delete',
        label: 'Delete',
        asset: trash,
        destructive: true,
        disabled: !!blockedWhy,
        disabledReason: blockedWhy || undefined,
        onSelect: () => void remove([row]),
      },
    ];
  };

  const bulkActions = (rows: T[]): LabRowAction[] =>
    rows.length
      ? [
          ...(config.copyOf
            ? [
                {
                  key: 'duplicate',
                  label: 'Duplicate',
                  onSelect: () => void duplicate(rows),
                },
              ]
            : []),
          { key: 'archive', label: 'Archive', onSelect: () => void setEnabled(rows, false) },
          { key: 'restore', label: 'Restore', onSelect: () => void setEnabled(rows, true) },
          {
            key: 'delete',
            label: 'Delete',
            destructive: true,
            onSelect: () => void remove(rows),
          },
        ]
      : [];

  return { rowActions, bulkActions, busy: save.isPending || destroy.isPending };
};

/** The Edit and Open entries, which every view puts above the lifecycle ones. */
const openActions = <T,>(
  row: T,
  onEdit?: (row: T) => void,
  onOpen?: () => void,
  openLabel = 'Open',
): LabRowAction[] => [
  ...(onEdit
    ? [{ key: 'edit', label: 'Edit', asset: pencilSimple, onSelect: () => onEdit(row) }]
    : []),
  ...(onOpen
    ? [{ key: 'open', label: openLabel, asset: arrowSquareOut, onSelect: onOpen }]
    : []),
];

/** Two lines: what it is called, and the handle everything else refers to it by. */
const nameCell = (name: string, handle?: string, note?: string) => (
  <>
    <div className="font-medium">{name || '-'}</div>
    {handle && <div className="font-mono text-xs text-gray-500">{handle}</div>}
    {note && <div className="text-xs text-amber-800">{note}</div>}
  </>
);

const countLabel = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

/* ------------------------------------------------------------------------- *
 * Labs
 * ------------------------------------------------------------------------- */

/**
 * The labs.
 *
 * The one tier with no parents, so its blast-radius column asks the other question: not what
 * contains this, but where it is delivered. A lab published in two data centres is two pods, two
 * demo ids and two sets of address overrides, and that is what an edit here reaches.
 *
 * List rows do not nest their sections or publications, so both are read from the link tables and
 * hydrated back onto the row before `labBlockers` sees it - a lab judged on the arrays it happens
 * to have been served would report every list row as unpublished.
 */
export const LabsLibraryView: React.FC<LibraryViewProps<Lab>> = ({
  canEdit = false,
  onNew,
  onEdit,
  openLab,
  selectedId,
  searchTerm,
  onSearchTerm,
}) => {
  const { data: labs, isLoading, error } = useLabs();
  const { data: links } = useLabScenarioLinks();
  const { data: configurations } = useLabConfigurations();
  const { data: dataCenters } = useLabDataCenters();

  const sectionsByLab = useMemo(() => {
    const byLab = new Map<number, LabScenarioLink[]>();

    (links || []).forEach((link) => {
      byLab.set(link.lab, [...(byLab.get(link.lab) || []), link]);
    });

    return byLab;
  }, [links]);

  const publicationsByLab = useMemo(() => {
    const byLab = new Map<number, LabConfiguration[]>();

    (configurations || []).forEach((configuration) => {
      if (!configuration.lab) return;

      byLab.set(configuration.lab, [...(byLab.get(configuration.lab) || []), configuration]);
    });

    return byLab;
  }, [configurations]);

  const lifecycle = useLifecycle<Lab>(LAB_TABLES.lab, {
    nameOf: (lab) => lab.name,
    deleteConsequence:
      'Its publications go with it, and so does every device address override written for them. The scenarios it composes are shared and stay - only the sections that attached them are removed.',
    copyOf: (lab) => ({
      name: `${lab.name} (copy)`,
      slug: '',
      description: lab.description,
      // The copy is built from the same dCloud demo: a lab copied to be varied is a variation of
      // that environment, and re-picking the demo it plainly runs on would be busywork.
      demo_uid: lab.demo_uid,
      demo_name: lab.demo_name,
      demo_data_center: lab.demo_data_center,
      owner_email: lab.owner_email,
      // A copy is not published anywhere and has no sections yet, so it delivers nothing until
      // somebody attaches them. Left disabled rather than live-and-empty.
      enabled: false,
      metadata: lab.metadata,
    }),
  });

  const facets: LabLibraryFacet<Lab>[] = useMemo(
    () => [
      {
        key: 'dataCentre',
        label: 'Data centre',
        options: (dataCenters || []).map((dc) => ({
          value: String(dc.id),
          label: dc.dcloud_dc_name,
        })),
        matches: (lab, value) =>
          (publicationsByLab.get(lab.id) || []).some(
            (configuration) => String(configuration.data_center) === value,
          ),
      },
      {
        key: 'size',
        label: 'Pod size',
        options: LAB_SIZES as LabOption<string>[],
        matches: (lab, value) =>
          (publicationsByLab.get(lab.id) || []).some(
            (configuration) => configuration.size === value,
          ),
      },
    ],
    [dataCenters, publicationsByLab],
  );

  return (
    <LabLibraryList<Lab>
      searchTerm={searchTerm}
      onSearchTerm={onSearchTerm}
      title="Labs"
      subtitle="The top level. A lab is a composition of sections plus the data centres it is published in - one dCloud topology each."
      rows={labs || []}
      loading={isLoading}
      error={error}
      emptyMessage="No labs yet. A lab is what a learner is given; the sections in it are shared with every other lab that needs them."
      selectedId={selectedId}
      onRowClick={openLab ? (lab) => openLab(lab.id) : undefined}
      nameOf={(lab) =>
        nameCell(lab.name, lab.slug, lab.demo_uid ? undefined : 'No dCloud demo linked')
      }
      search={labMatchesLab}
      facets={facets}
      columns={[
        {
          key: 'sections',
          header: 'Sections',
          render: (lab) => {
            const all = sectionsByLab.get(lab.id) || [];
            const live = all.filter(
              (link) => link.enabled && link.scenario_enabled !== false,
            );

            if (!all.length) return <span className="text-xs text-gray-500">None</span>;

            return (
              <>
                <div>{countLabel(live.length, 'live section')}</div>
                {live.length !== all.length && (
                  <div className="text-xs text-amber-800">
                    {`${all.length - live.length} switched off`}
                  </div>
                )}
              </>
            );
          },
        },
        {
          key: 'owner',
          header: 'Owner',
          render: (lab) => (
            <>
              <div className="text-xs">{lab.owner_email || 'Nobody named'}</div>
              {/* The demo it is built from, read against the data centre the id belongs to - a
                  uid on its own names a different demo in every other data centre. */}
              {!!lab.demo_uid && (
                <div className="text-xs text-gray-500">
                  {labDemoSummary(
                    lab,
                    (dataCenters || []).find((dc) => dc.id === lab.demo_data_center) || null,
                  )}
                </div>
              )}
            </>
          ),
        },
      ]}
      statusOf={(lab) => {
        const sections = sectionsByLab.get(lab.id) || [];
        const live = sections.filter((link) => link.enabled && link.scenario_enabled !== false);
        const published = publicationsByLab.get(lab.id) || [];
        const blockers: string[] = [];

        if (!live.length) {
          blockers.push(
            sections.length
              ? 'Every section is switched off, so runs open and do nothing.'
              : 'No sections, so this delivers the bare dCloud template.',
          );
        }
        if (!published.length) {
          blockers.push('Not published in any data centre, so no session is ever matched to it.');
        }
        // Not a blocker for a run - what a session is matched on is the demo id on each
        // publication, listed above. It is a blocker for anyone maintaining the lab: with no
        // demo recorded, nothing says which dCloud environment it was authored against.
        if (!lab.demo_uid) {
          blockers.push(
            'No dCloud demo recorded, so nothing says which environment this lab was built from.',
          );
        }

        return authoringStatus(
          lab.enabled,
          blockers,
          !live.length || !published.length,
          'Disabled. Sessions are not matched against it at all.',
        );
      }}
      usageHeader="Delivered in"
      usageEmptyLabel="Nowhere"
      usageSubjectOf={(lab) => lab.name}
      usagesOf={(lab) =>
        (publicationsByLab.get(lab.id) || []).map<LabUsage>((configuration) => ({
          key: `publication-${configuration.id}`,
          kind: 'Publication',
          label: configuration.data_center_name || `data centre ${configuration.data_center}`,
          hint: [
            configuration.demo_uid ? `demo ${configuration.demo_uid}` : 'no demo id - never matched',
            labSizeLabel(configuration.size),
            (configuration.device_addresses || []).length
              ? countLabel((configuration.device_addresses || []).length, 'address override')
              : null,
          ]
            .filter(Boolean)
            .join(' • '),
          inactive: !configuration.enabled,
        }))
      }
      updatedOf={updatedOf}
      newLabel="New lab"
      onNew={canEdit ? onNew : undefined}
      rowActions={
        canEdit
          ? (lab) => [
              ...openActions(
                lab,
                onEdit,
                openLab ? () => openLab(lab.id) : undefined,
                'Open in builder',
              ),
              ...lifecycle.rowActions(lab),
            ]
          : undefined
      }
      bulkActions={canEdit ? lifecycle.bulkActions : undefined}
    />
  );
};

/* ------------------------------------------------------------------------- *
 * Scenarios
 * ------------------------------------------------------------------------- */

/**
 * The scenarios, as a library rather than as one lab's sections.
 *
 * This is the view the reuse tier exists for. A scenario is a section of however many labs link
 * it, and the count in "Used in" is the whole argument for editing it carefully - so the modal
 * names the labs, and marks the links that are switched off at either end, because a section
 * attached to a disabled lab is reach that does not currently exist.
 *
 * `legacy_configurations` are listed as usages too. They are publications written before the lab
 * tier and they still match sessions, so leaving them out of the blast radius would understate it
 * on exactly the rows where it matters most.
 */
export const ScenariosLibraryView: React.FC<LibraryViewProps<LabScenario>> = ({
  canEdit = false,
  onNew,
  onEdit,
  openScenario,
  openLab,
  selectedId,
  searchTerm,
  onSearchTerm,
}) => {
  const { data: scenarios, isLoading, error } = useLabScenarios();
  const { data: links } = useLabScenarioLinks();
  const { data: configurations } = useLabConfigurations();

  const linksByScenario = useMemo(() => {
    const byScenario = new Map<number, LabScenarioLink[]>();

    (links || []).forEach((link) => {
      byScenario.set(link.scenario, [...(byScenario.get(link.scenario) || []), link]);
    });

    return byScenario;
  }, [links]);

  /** Publications still bound straight to a scenario. Nothing new should create one. */
  const legacyByScenario = useMemo(() => {
    const byScenario = new Map<number, LabConfiguration[]>();

    (configurations || []).forEach((configuration) => {
      if (configuration.lab || !configuration.scenario) return;

      byScenario.set(configuration.scenario, [
        ...(byScenario.get(configuration.scenario) || []),
        configuration,
      ]);
    });

    return byScenario;
  }, [configurations]);

  const liveLinks = (scenario: LabScenario) =>
    (linksByScenario.get(scenario.id) || []).filter(
      (link) => link.enabled && link.lab_enabled !== false,
    );

  const lifecycle = useLifecycle<LabScenario>(LAB_TABLES.scenario, {
    nameOf: (scenario) => scenario.name,
    deleteConsequence:
      'Its devices, steps, success criteria and item attachments go with it. The configuration items themselves are catalogue rows and stay.',
    // No duplicate. A scenario is its devices, steps, criteria and attachments, and copying the
    // one row would produce an empty scenario wearing the name of a full one - which reads as a
    // working copy on every screen that lists it. Duplicating properly is a server-side clone.
    deleteBlockedBy: (scenario) => {
      const attached = (linksByScenario.get(scenario.id) || []).length;

      return attached
        ? `${countLabel(attached, 'lab')} still link it. Detach it there first - the API refuses this too.`
        : null;
    },
  });

  const facets: LabLibraryFacet<LabScenario>[] = useMemo(
    () => [
      {
        key: 'reach',
        label: 'Reach',
        options: [
          { value: 'attached', label: 'In at least one live lab' },
          { value: 'switched-off', label: 'Linked, but nothing live' },
          { value: 'orphan', label: 'In no lab at all' },
          { value: 'legacy', label: 'Published directly (pre-lab)' },
        ],
        matches: (scenario, value) => {
          const all = linksByScenario.get(scenario.id) || [];
          const live = liveLinks(scenario);
          const legacy = legacyByScenario.get(scenario.id) || [];

          if (value === 'attached') return !!live.length;
          if (value === 'switched-off') return !!all.length && !live.length;
          if (value === 'orphan') return !all.length;

          return !!legacy.length;
        },
      },
      {
        key: 'opens',
        label: 'How runs open',
        options: [
          { value: 'auto', label: 'Automatically on session start' },
          { value: 'manual', label: 'Only when asked' },
          { value: 'bootstrap', label: 'Bootstrap only - no learner work' },
        ],
        matches: (scenario, value) => {
          if (value === 'bootstrap') return !!scenario.bootstrap_only;
          if (value === 'auto') return !!scenario.auto_start && !scenario.bootstrap_only;

          return !scenario.auto_start && !scenario.bootstrap_only;
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [linksByScenario, legacyByScenario],
  );

  return (
    <LabLibraryList<LabScenario>
      searchTerm={searchTerm}
      onSearchTerm={onSearchTerm}
      title="Scenarios"
      subtitle="The reusable sections. A scenario holds the devices and the work; the labs that link it decide where that work is delivered."
      rows={scenarios || []}
      loading={isLoading}
      error={error}
      emptyMessage="No scenarios yet. A scenario is a section of a lab, and the same one can be a section of many."
      selectedId={selectedId}
      onRowClick={openScenario ? (scenario) => openScenario(scenario.id) : undefined}
      nameOf={(scenario) => nameCell(scenario.name, scenario.slug)}
      search={labMatchesScenario}
      facets={facets}
      columns={[
        {
          key: 'opens',
          header: 'How runs open',
          render: (scenario) => (
            <div className="flex flex-wrap gap-1">
              {scenario.bootstrap_only ? (
                <LabStatusTag label="Bootstrap only" sentiment="info" />
              ) : scenario.auto_start ? (
                <LabStatusTag label="Auto start" sentiment="success" />
              ) : (
                <LabStatusTag label="On request" />
              )}
              {!scenario.bootstrap_only && (
                <span className="text-xs text-gray-500">
                  {`pass at ${scenario.pass_threshold_percent}%`}
                </span>
              )}
            </div>
          ),
        },
        {
          key: 'version',
          header: 'Version',
          render: (scenario) => (
            <>
              <div className="text-xs">{`v${scenario.version}`}</div>
              {scenario.course_name && (
                <div className="text-xs text-gray-500">{scenario.course_name}</div>
              )}
            </>
          ),
        },
      ]}
      statusOf={(scenario) => {
        const all = linksByScenario.get(scenario.id) || [];
        const live = liveLinks(scenario);
        const legacy = legacyByScenario.get(scenario.id) || [];
        const blockers: string[] = [];

        if (!all.length && !legacy.length) {
          blockers.push('In no lab, so no session reaches it. Attach it to a lab.');
        } else if (!live.length && !legacy.length) {
          blockers.push(
            `Linked by ${countLabel(all.length, 'lab')}, all switched off at one end or the other.`,
          );
        }

        if (legacy.length && !all.length) {
          blockers.push(
            'Published directly to a data centre, which predates the lab tier. Move it under a lab.',
          );
        }

        return authoringStatus(
          scenario.enabled,
          blockers,
          !live.length && !legacy.length,
          'Disabled. It stays linked to its labs and compiles to nothing.',
        );
      }}
      usageSubjectOf={(scenario) => scenario.name}
      usageEmptyLabel="No lab"
      usagesOf={(scenario) => [
        ...(linksByScenario.get(scenario.id) || []).map<LabUsage>((link) => ({
          key: `lab-${link.id}`,
          kind: 'Lab',
          label: link.lab_name || `lab ${link.lab}`,
          hint: !link.enabled
            ? 'The section is switched off in that lab'
            : link.lab_enabled === false
              ? 'That lab is disabled'
              : `section ${link.order}`,
          inactive: !link.enabled || link.lab_enabled === false,
          onOpen: openLab ? () => openLab(link.lab) : undefined,
        })),
        ...(legacyByScenario.get(scenario.id) || []).map<LabUsage>((configuration) => ({
          key: `legacy-${configuration.id}`,
          kind: 'Publication (pre-lab)',
          label: configuration.data_center_name || configuration.name,
          hint: 'Bound straight to this scenario, from before labs existed. Still matches sessions.',
          inactive: !configuration.enabled,
        })),
      ]}
      updatedOf={updatedOf}
      newLabel="New scenario"
      onNew={canEdit ? onNew : undefined}
      rowActions={
        canEdit
          ? (scenario) => [
              ...openActions(
                scenario,
                onEdit,
                openScenario ? () => openScenario(scenario.id) : undefined,
                'Open in builder',
              ),
              ...lifecycle.rowActions(scenario),
            ]
          : undefined
      }
      bulkActions={canEdit ? lifecycle.bulkActions : undefined}
    />
  );
};

/* ------------------------------------------------------------------------- *
 * Configuration items
 * ------------------------------------------------------------------------- */

/**
 * The catalogue of configuration items.
 *
 * The most shared table in the schema and the one where an edit travels furthest: an item attached
 * by nine scenarios is nine scenarios' worth of recompilation, and the row itself only says "9".
 * So the attachments are loaded whole and the usage modal names the scenario *and the device*, since
 * the same item is routinely attached twice to configure both firewalls and which device it landed
 * on is the part worth checking.
 */
export const ConfigurationItemsLibraryView: React.FC<
  LibraryViewProps<LabConfigurationItem>
> = ({
  canEdit = false,
  onNew,
  onEdit,
  openItem,
  openScenario,
  selectedId,
  searchTerm,
  onSearchTerm,
}) => {
  const { data: items, isLoading, error } = useLabConfigurationItems();
  const { data: attachments } = useLabAttachments();
  const { data: scenarios } = useLabScenarios();
  const { data: deviceTypes } = useLabDeviceTypes();

  const scenarioName = useMemo(() => {
    const byId = new Map<number, string>();

    (scenarios || []).forEach((scenario) => byId.set(scenario.id, scenario.name));

    return byId;
  }, [scenarios]);

  const attachmentsByItem = useMemo(() => {
    const byItem = new Map<number, LabScenarioItem[]>();

    (attachments || []).forEach((attachment) => {
      byItem.set(attachment.item, [...(byItem.get(attachment.item) || []), attachment]);
    });

    return byItem;
  }, [attachments]);

  const lifecycle = useLifecycle<LabConfigurationItem>(LAB_TABLES.configurationItem, {
    nameOf: (item) => item.name,
    deleteConsequence:
      'Every attachment of it, in every scenario, goes with it - and so do the steps those attachments compiled into, the next time each scenario is compiled.',
    copyOf: (item) => ({
      key: `${item.key}_copy`,
      name: `${item.name} (copy)`,
      description: item.description,
      operation: item.operation,
      device_type: item.device_type,
      options: item.options,
      action: item.action,
      action_method: item.action_method,
      validation: item.validation,
      validation_method: item.validation_method,
      health_check: item.health_check,
      health_method: item.health_method,
      completion_criteria: item.completion_criteria,
      completion_method: item.completion_method,
      pre_configure: item.pre_configure,
      // Prerequisites are copied: a variant of an item needs the same things done first, and a
      // copy that quietly lost its chain would compile into a step that runs too early.
      prerequisites: item.prerequisites,
      order: item.order,
      enabled: false,
      metadata: item.metadata,
    }),
  });

  const facets: LabLibraryFacet<LabConfigurationItem>[] = useMemo(
    () => [
      {
        key: 'operation',
        label: 'Operation',
        options: LAB_ITEM_OPERATIONS as LabOption<string>[],
        matches: (item, value) => item.operation === value,
      },
      {
        key: 'deviceType',
        label: 'Written for',
        options: [
          ...(deviceTypes || []).map((type) => ({ value: String(type.id), label: type.name })),
          { value: 'none', label: 'Nothing in particular' },
        ],
        matches: (item, value) =>
          value === 'none' ? !item.device_type : String(item.device_type) === value,
      },
    ],
    [deviceTypes],
  );

  return (
    <LabLibraryList<LabConfigurationItem>
      searchTerm={searchTerm}
      onSearchTerm={onSearchTerm}
      title="Configuration items"
      subtitle="The catalogue. One item is one piece of work, written once and attached by every scenario that needs it."
      rows={items || []}
      loading={isLoading}
      error={error}
      emptyMessage="The catalogue is empty. An item is a piece of work - bring up a tunnel, break a rule, check a route - written against a kind of device."
      selectedId={selectedId}
      onRowClick={openItem ? (item) => openItem(item.id) : undefined}
      nameOf={(item) => nameCell(item.name, item.key)}
      search={labMatchesItem}
      facets={facets}
      columns={[
        {
          key: 'what',
          header: 'What it does',
          render: (item) => (
            <>
              <div className="text-xs">{labItemSummary(item)}</div>
              {item.pre_configure && (
                <LabStatusTag label="Already in the template" sentiment="info" />
              )}
            </>
          ),
        },
        {
          key: 'prereqs',
          header: 'Runs after',
          render: (item) => {
            const keys = item.prerequisite_keys || [];

            if (!keys.length) return <span className="text-xs text-gray-500">Nothing</span>;

            return (
              <div className="text-xs" title={`Attaching this attaches: ${keys.join(', ')}`}>
                {countLabel(keys.length, 'item')}
                <div className="font-mono text-gray-500">{keys.slice(0, 2).join(', ')}</div>
              </div>
            );
          },
        },
      ]}
      statusOf={(item) => {
        const blockers = labItemBlockers(item);
        const compilesToNothing =
          !Object.keys(item.action || {}).length && !Object.keys(item.validation || {}).length;

        return authoringStatus(
          item.enabled,
          blockers,
          compilesToNothing,
          'Disabled. It stays attached everywhere and compiles to nothing.',
        );
      }}
      usageSubjectOf={(item) => item.name}
      usageEmptyLabel="Not attached"
      usagesOf={(item) =>
        (attachmentsByItem.get(item.id) || []).map<LabUsage>((attachment) => ({
          key: `attachment-${attachment.id}`,
          kind: 'Scenario',
          label:
            scenarioName.get(attachment.scenario) || `scenario ${attachment.scenario}`,
          hint: [
            `on ${attachment.device_name || attachment.device_key || `device ${attachment.device}`}`,
            attachment.added_as_prerequisite
              ? `pulled in by ${attachment.required_by_key || 'another item'}`
              : null,
            Object.keys(attachment.option_overrides || {}).length
              ? countLabel(Object.keys(attachment.option_overrides || {}).length, 'option override')
              : null,
          ]
            .filter(Boolean)
            .join(' • '),
          inactive: !attachment.enabled,
          onOpen: openScenario ? () => openScenario(attachment.scenario) : undefined,
        }))
      }
      updatedOf={updatedOf}
      newLabel="New item"
      onNew={canEdit ? onNew : undefined}
      rowActions={
        canEdit
          ? (item) => [
              ...openActions(item, onEdit, openItem ? () => openItem(item.id) : undefined),
              ...lifecycle.rowActions(item),
            ]
          : undefined
      }
      bulkActions={canEdit ? lifecycle.bulkActions : undefined}
    />
  );
};

/* ------------------------------------------------------------------------- *
 * Devices
 * ------------------------------------------------------------------------- */

/**
 * The device library.
 *
 * A shared catalogue in exactly the way the item one is: every dCloud pod is built from the same
 * template, so a box is authored once and attached to every section that works on it. Which makes
 * the "Sections" column and the usage badge the load-bearing part of the row - an edit here reaches
 * every one of them, and every lab those sections are in.
 *
 * It is still the estate view as well: which boxes exist, how each is reached, and which of them
 * nothing can currently connect to. The address shown is the device's own; a publication can
 * override it per data centre for the pod that came up different, which is why a blank address is
 * reported as a warning in the status title rather than as a certain failure.
 *
 * `credential_ref` is displayed as the name of a credential, because that is all it ever is. No
 * screen in this tree shows or accepts a secret.
 */
export const DevicesLibraryView: React.FC<LibraryViewProps<LabDevice>> = ({
  canEdit = false,
  onNew,
  onEdit,
  openDevice,
  openScenario,
  selectedId,
  searchTerm,
  onSearchTerm,
}) => {
  const { data: devices, isLoading, error } = useLabDevices();
  const { data: scenarios } = useLabScenarios();
  const { data: attachments } = useLabAttachments();
  const { data: deviceTypes } = useLabDeviceTypes();

  const scenarioName = useMemo(() => {
    const byId = new Map<number, string>();

    (scenarios || []).forEach((scenario) => byId.set(scenario.id, scenario.name));

    return byId;
  }, [scenarios]);

  const attachmentsByDevice = useMemo(() => {
    const byDevice = new Map<number, LabScenarioItem[]>();

    (attachments || []).forEach((attachment) => {
      byDevice.set(attachment.device, [...(byDevice.get(attachment.device) || []), attachment]);
    });

    return byDevice;
  }, [attachments]);

  const deviceById = useMemo(
    () => new Map((devices || []).map((device) => [device.id, device])),
    [devices],
  );

  const lifecycle = useLifecycle<LabDevice>(LAB_TABLES.device, {
    nameOf: (device) => device.display_name || device.device_key,
    deleteConsequence:
      'It is removed from every section that has it, and so from every lab those sections are in. Every item attached to it is detached, the steps those compiled into stop being generated, and any per-publication address override for it goes too.',
    // Attached to nothing. A copy is a second box, and inheriting the original's sections would put
    // an untouched duplicate into live labs - the sections are ticked in the editor instead.
    copyOf: (device) => ({
      device_key: `${device.device_key}_copy`,
      display_name: `${device.display_name} (copy)`,
      device_type: device.device_type,
      // Carried, because a copy of an FTD is an FTD: the type is a family and the product is what
      // says which member, so dropping it would leave the copy flagged for an answer it has.
      product_uid: device.product_uid,
      product_name: device.product_name,
      platform: device.platform,
      role: device.role,
      transport: device.transport,
      // Not the address. Two devices in one pod are not reached at the same place, and a copy
      // that inherited it would be a second row quietly pointing at the first box.
      address_template: '',
      port: device.port,
      credential_ref: device.credential_ref,
      jump_host: device.jump_host,
      verify_tls: device.verify_tls,
      bootstrap_expected: device.bootstrap_expected,
      required: device.required,
      monitored: device.monitored,
      order: device.order,
      attributes: device.attributes,
    }),
    deleteBlockedBy: (device) => {
      const attached = (attachmentsByDevice.get(device.id) || []).length;

      return attached
        ? `${countLabel(attached, 'item')} are applied to it. Detach them in the scenario first.`
        : null;
    },
  });

  const facets: LabLibraryFacet<LabDevice>[] = useMemo(
    () => [
      {
        key: 'deviceType',
        label: 'Device type',
        options: [
          ...(deviceTypes || []).map((type) => ({ value: String(type.id), label: type.name })),
          { value: 'none', label: 'Unclassified' },
        ],
        matches: (device, value) =>
          value === 'none' ? !device.device_type : String(device.device_type) === value,
      },
      {
        key: 'transport',
        label: 'Reached over',
        options: LAB_TRANSPORTS as LabOption<string>[],
        matches: (device, value) => device.transport === value,
      },
    ],
    [deviceTypes],
  );

  return (
    <LabLibraryList<LabDevice>
      searchTerm={searchTerm}
      onSearchTerm={onSearchTerm}
      title="Devices"
      subtitle="The boxes the work lands on, authored once. Every pod is built from the same dCloud template, so a device is attached to as many sections as use it - and where it is reached can still be overridden per publication, for the pod that came up different."
      rows={devices || []}
      loading={isLoading}
      error={error}
      emptyMessage="No devices recorded. A device is a box in the dCloud pod that items can be attached to."
      selectedId={selectedId}
      onRowClick={openDevice ? (device) => openDevice(device.id) : undefined}
      nameOf={(device) =>
        nameCell(
          device.display_name || device.device_key,
          device.device_key,
          device.device_type ? undefined : 'No device type - attachments unchecked',
        )
      }
      search={labMatchesDevice}
      facets={facets}
      columns={[
        {
          key: 'sections',
          header: 'Sections',
          // Named rather than counted, up to a point: "in 4 sections" does not tell somebody
          // whether the one they are about to edit is among them, and the full list is what the
          // usage badge is for.
          render: (device) => {
            const names = (device.scenario_links || []).map(
              (link) =>
                link.scenario_name ||
                scenarioName.get(link.scenario) ||
                `scenario ${link.scenario}`,
            );

            return (
              <>
                <div className="text-xs">
                  {names.length
                    ? names.slice(0, 2).join(', ') +
                      (names.length > 2 ? ` +${names.length - 2} more` : '')
                    : 'Not attached anywhere'}
                </div>
                {device.role && <div className="text-xs text-gray-500">{device.role}</div>}
              </>
            );
          },
        },
        {
          key: 'reached',
          header: 'Reached at',
          render: (device) => (
            <>
              <div className="font-mono text-xs break-all">
                {device.address_template || 'no address'}
                {device.port ? `:${device.port}` : ''}
              </div>
              <div className="text-xs text-gray-500">
                {[
                  labTransportLabel(device.transport),
                  device.credential_ref ? `as ${device.credential_ref}` : 'no credential named',
                ].join(' • ')}
              </div>
            </>
          ),
        },
        {
          key: 'type',
          header: 'Type',
          render: (device) => (
            <>
              <div className="text-xs">{device.device_type_name || 'Unclassified'}</div>
              {/* Which product, for a family type. The API a step is generated against comes from
                  here, so an unanswered one is worth reading on the row. */}
              {device.device_type_requires_product && (
                <div
                  className={`text-xs ${
                    device.product_uid ? 'text-gray-500' : 'text-amber-800'
                  }`}
                >
                  {device.product_name || 'no product chosen'}
                </div>
              )}
              {device.platform && (
                <div className="text-xs text-gray-500">{device.platform}</div>
              )}
            </>
          ),
        },
      ]}
      statusOf={(device) => {
        // The library is the one screen with every device in hand, which is what the jump-host
        // check needs: it compares the sections this box is attached to against its route's.
        const blockers = labDeviceBlockers(device, {
          jumpHost: device.jump_host ? deviceById.get(device.jump_host) || null : null,
        });

        return authoringStatus(
          // Devices have no enabled flag - `required` and `monitored` are the switches, and
          // neither retires a row - so nothing here is ever reported as archived.
          true,
          blockers,
          !(device.address_template || '').trim(),
          '',
        );
      }}
      usageSubjectOf={(device) => device.display_name || device.device_key}
      usageEmptyLabel="Nowhere"
      // Two tiers in one list, sections first: the blast radius of a device edit is every section
      // that attaches it, and the items applied to it are what that edit could break. A count of
      // items alone read as "nothing uses this" for a box four sections share.
      usagesOf={(device) => [
        ...(device.scenario_links || []).map<LabUsage>((link) => ({
          key: `section-${link.id}`,
          kind: 'Section',
          label: link.scenario_name || scenarioName.get(link.scenario) || `scenario ${link.scenario}`,
          hint: link.enabled
            ? 'Attached. Every lab with this section gets this box.'
            : 'Attachment switched off: the section keeps the row and compiles without the device.',
          inactive: !link.enabled || link.scenario_enabled === false,
          onOpen: openScenario ? () => openScenario(link.scenario) : undefined,
        })),
        ...(attachmentsByDevice.get(device.id) || []).map<LabUsage>((attachment) => ({
          key: `attachment-${attachment.id}`,
          kind: 'Item',
          label: attachment.item_name || attachment.item_key || `item ${attachment.item}`,
          hint: [
            scenarioName.get(attachment.scenario) || `scenario ${attachment.scenario}`,
            attachment.item_operation ? labItemOperationLabel(attachment.item_operation) : null,
            attachment.item_device_type_key &&
            attachment.device_type_key &&
            attachment.item_device_type_key !== attachment.device_type_key
              ? `written for ${attachment.item_device_type_key}, not ${attachment.device_type_key}`
              : null,
          ]
            .filter(Boolean)
            .join(' • '),
          inactive: !attachment.enabled || attachment.item_enabled === false,
          onOpen: openScenario ? () => openScenario(attachment.scenario) : undefined,
        })),
      ]}
      updatedOf={updatedOf}
      newLabel="New device"
      onNew={canEdit ? onNew : undefined}
      rowActions={
        canEdit
          ? (device) => [
              ...openActions(device, onEdit, openDevice ? () => openDevice(device.id) : undefined),
              // Archive is dropped here and nowhere else: a device has no `enabled` column - its
              // switches are `required` and `monitored`, and neither retires the row - so the entry
              // would either do nothing or invent a field. Duplicate and Delete are the real ones.
              ...lifecycle.rowActions(device).filter((action) => action.key !== 'enabled'),
            ]
          : undefined
      }
      bulkActions={
        canEdit
          ? (rows) =>
              lifecycle
                .bulkActions(rows)
                .filter((action) => action.key !== 'archive' && action.key !== 'restore')
          : undefined
      }
    />
  );
};
